// Angular
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// PrimeNG
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import {
  DialogService,
  DynamicDialogModule,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
// Environment
import { environment } from '@env/environment';
// Rxjs
import { distinctUntilChanged, filter, map } from 'rxjs';
// Models
import { ControlsOf } from '@shared/models-v3/controls-of.type';
import { InvoiceForm } from './models/invoice-form.interface';
import { ICustomerAddress } from '@core/models-v2/customer/customer.interface';
import { InvoiceForms } from './models/invoice-forms.interface';
import { DocumentIdAsyncValidationType } from '@shared/components-v3/atomic/document-id-input/models/async-validation-type.type';
import { BusinessLine } from '@core/services-v2/auth/models/check-business-response.dto';
// Components
import { DocumentIdInputComponent } from '@shared/components-v3/atomic/document-id-input/document-id-input.component';
import { EmailInputComponent } from '@shared/components-v3/atomic/email-input/email-input.component';
import { InvoiceAddressFormComponent } from './components/invoice-address-form/invoice-address-form.component';
// Directives
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
// Validators
import { BusinessValidator } from '@shared/components-v3/atomic/document-id-input/validators/business.validator';
// Services
import { SessionService } from '@core/services-v2/session/session.service';
//import { CustomerBusinessLineApiService } from '@core/services-v2/customer-business-line/customer-business-line.api.service';
import { CommonModule } from '@angular/common';
import { CustomerBusinessLineApiService } from '@core/services-v2/customer-business-line/customer-business-line.api.service';
import { AddressDto } from '@core/services-v2/customer-address/dto/create-address.dto';

const TEMP = [CommonModule];
const NG_MODULES = [ReactiveFormsModule];
const PRIME_MODULES = [
  IconFieldModule,
  InputIconModule,
  InputTextModule,
  DropdownModule,
  DynamicDialogModule,
];
const COMPONENTS = [
  DocumentIdInputComponent,
  EmailInputComponent,
  InvoiceAddressFormComponent,
];
const DIRECTIVES = [FormSubmitDirective, ControlErrorsDirective];

@Component({
  selector: 'app-invoice-form',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, COMPONENTS, DIRECTIVES, TEMP],
  templateUrl: './invoice-form.component.html',
  styleUrl: './invoice-form.component.scss',
  providers: [DialogService],
})
export class InvoiceFormComponent implements OnInit {
  //#region ViewChild elements
  @ViewChild('businessNameInputElement')
  businessNameInputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('businessLineInputElement') businessLineInputElement!: Dropdown;
  @ViewChild('invoiceFormElement') invoiceFormElement!: ElementRef;
  //#endregion

  //#region ViewChild components
  @ViewChild('invoiceAddressFormComponent')
  invoiceAddressFormComponent!: InvoiceAddressFormComponent;
  //#endregion

  //#region DocumentId input properties
  isLoading = signal<boolean>(false);
  hasAsyncData = signal<boolean>(false);
  lastValue = signal<string>('');
  businessName = signal<string>('');
  businessLines = signal<BusinessLine[]>([]);
  businessAlreadyExists = signal<boolean>(false);
  businessEmail = signal<string>('');
  invoiceAddress = signal<ICustomerAddress | null>(null);
  internalBusinessLines = signal<BusinessLine[]>([]);
  //#endregion

  token: string = '';
  invoiceForm!: FormGroup<ControlsOf<InvoiceForm>>;
  documentIdLabel = environment.documentId.businessLabel;
  documentIdSample = environment.documentId.businessSample;
  usePublicService = environment.documentId.usePublicService;
  documentIdAsyncValidationType!: DocumentIdAsyncValidationType;
  useDeliveryAddress = signal<boolean>(true);
  isLoadedBusiness = signal<boolean>(false);
  isRegisteredBusiness = signal<boolean>(false);

  //#region Getters
  get documentIdField(): FormControl<string> {
    return this.invoiceForm.controls.documentId;
  }

  get businessNameField(): FormControl<string> {
    return this.invoiceForm.controls.businessName;
  }

  get businessLineField(): FormControl<string> {
    return this.invoiceForm.controls.businessLineCode;
  }

  get emailField(): FormControl<string> {
    return this.invoiceForm.controls.email;
  }
  //#endregion

  //#region Dependencies
  private readonly fb = inject(NonNullableFormBuilder);
  public readonly sessionService = inject(SessionService);
  private readonly dialogService = inject(DialogService);
  private readonly customerBusinessLineApiService = inject(
    CustomerBusinessLineApiService
  );
  //#endregion

  //#region Lifecycle hooks
  ngOnInit(): void {
    if (this.sessionService.isEnterprise()) return;
    this.buildForm();
    this.setBusinessInput();
  }
  //#endregion

  /*
  private openCaptchaModal(): void {
    this.dialogService.open(CaptchaModalComponent, {
      header: '',
      width: '50vw',
      modal: true,
      data: {},
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
    });
  }*/

  /**
   * Configurar el input de documento de identificación de la empresa.
   */
  private setBusinessInput(): void {
    this.documentIdAsyncValidationType = 'business';
    this.hasAsyncData = BusinessValidator.hasAsyncData;
    this.isLoading = BusinessValidator.isLoading;
    this.businessName = BusinessValidator.businessName;
    this.businessLines = BusinessValidator.businessLines;
    this.businessEmail = BusinessValidator.businessEmail;
    this.businessAlreadyExists = BusinessValidator.alreadyExists;
    this.invoiceAddress = BusinessValidator.invoiceAddress;
    this.lastValue = BusinessValidator.lastValue;
    if (!this.usePublicService) {
      this.fetchInternalBusinessLines();
    }
  }

  private buildForm(): void {
    this.invoiceForm = this.fb.group({
      email: '',
      documentId: '',
      businessName: [
        {
          value: '',
          disabled: this.usePublicService,
        },
        Validators.required,
      ],
      businessLineName: ['', Validators.required],
      businessLineCode: ['', Validators.required],
    });
    this.onDocumentIdChange();
    this.onBusinessLineChange();
  }

  /**
   * Cuando el documento de identificación cambia.
   */
  private onDocumentIdChange(): void {
    this.documentIdField.valueChanges
      .pipe(
        filter((value) => !!this.lastValue() && this.lastValue() !== value),
        map(() => this.resetBusinessFields())
      )
      .subscribe();

    this.documentIdField.statusChanges
      .pipe(
        filter((status) => {
          return status === 'VALID' && this.hasAsyncData();
        }),
        map(() => this.updateBusinessFields())
      )
      .subscribe();
  }

  /**
   * Actualizar los campos de la empresa.
   */
  private updateBusinessFields(): void {
    if (!this.businessLines().length) {
      this.businessLines.set(this.internalBusinessLines());
    }

    const selectedBusinessLine = this.businessLines().length
      ? this.businessLines()[0]
      : null;

    this.invoiceForm.patchValue({
      email: this.businessEmail(),
      businessName: this.businessName() || '',
      businessLineCode: selectedBusinessLine?.code || '',
      businessLineName: selectedBusinessLine?.name || '',
    });

    this.isRegisteredBusiness.set(this.businessAlreadyExists());
    if (this.businessAlreadyExists()) {
      this.businessNameField.disable();
      this.businessLineField.disable();
      this.emailField.disable();
    } else {
      this.businessLineField.enable();
      this.emailField.enable();
    }

    if (this.usePublicService) {
      this.businessNameField.disable();
    } else {
      this.businessNameField.enable();
    }

    this.isLoadedBusiness.set(true);
    if (this.businessName()) {
      setTimeout(() => this.businessLineInputElement.focus(), 100);
    } else {
      setTimeout(
        () => this.businessNameInputElement.nativeElement.focus(),
        100
      );
    }
  }

  /**
   * Reiniciar los campos de la empresa.
   */
  private resetBusinessFields(): void {
    BusinessValidator.resetBusinessFields();
    this.invoiceForm.patchValue({
      businessName: '',
      businessLineCode: '',
      businessLineName: '',
      email: '',
    });
    this.isLoadedBusiness.set(false);
  }

  /**
   * Al cambiar el código del giro comercial desde el selector, se actualiza el nombre del giro comercial en el formulario.
   */
  private onBusinessLineChange(): void {
    this.businessLineField.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value) => {
        const businessLineItem = this.businessLines().find(
          (line) => line.code === value
        );
        this.invoiceForm.patchValue({
          businessLineName: businessLineItem?.name,
        });
      });
  }

  onUseDeliveryChange(value: boolean): void {
    this.useDeliveryAddress.set(value);
  }

  /**
   * Dispara el evento submit del formulario y valida sus estado.
   * - Este método es llamado desde el componente padre.
   */
  submit(): InvoiceForms | null {
    // Invoice Business
    let business: InvoiceForm | null = null;
    let isValidInvoiceForm = true;
    if (!this.sessionService.isEnterprise()) {
      this.invoiceFormElement.nativeElement.dispatchEvent(
        new Event('submit', { bubbles: true, cancelable: true })
      );
      isValidInvoiceForm = this.invoiceForm.valid;
      if (isValidInvoiceForm) {
        business = this.invoiceForm.getRawValue();
      }
    }

    // Invoice Address
    let isValidInvoiceAddressForm = false;
    let invoiceAddress: AddressDto | null = null;
    let invoiceAddressId: string | null = null;

    if (this.isLoadedBusiness()) {
      const invoiceAddressFormValues =
        this.invoiceAddressFormComponent.submit();
      isValidInvoiceAddressForm = invoiceAddressFormValues.isValid;
      invoiceAddress = invoiceAddressFormValues.invoiceAddress;
      invoiceAddressId = invoiceAddressFormValues.invoiceAddressId;
    }

    return isValidInvoiceForm && isValidInvoiceAddressForm
      ? {
          invoiceAddress,
          invoiceAddressId,
          business, //: this.invoiceForm.getRawValue(),
          isRegistered: this.isRegisteredBusiness(),
        }
      : null;
  }

  /**
   * Dispara el evento submit del formulario.
   */
  nativeSubmit(): void {
    if (this.invoiceForm.valid) return;
    this.invoiceForm.markAllAsTouched();
  }

  /**
   * Obtener información de empresa a partir del documento de identificación.
   * @param documentId - Documento de identificación de la empresa.
   * @returns
   */
  /*getBusiness(documentId: string): void {
    if (this.documentIdField.invalid) return;
    //this.isLoadingBusiness.set(true);
    this.customerBusinessLineApiService.getBusiness(documentId).subscribe({
      next: (response) => {
        console.log('business response: ', response);
        //this.isLoadedBusiness.set(true);

        this.businessLines.set(response.businessLines);
        //this.invoiceAddresses.set(response.invoiceAddresses);
        this.invoiceForm.patchValue({
          businessName: response.businessName,
          businessLineCode: response.businessLines[0].code,
          businessLineName: response.businessLines[0].name,
          email: response.email || '',
        });
        if (response.exists) {
          this.businessLineField.disable();
          this.emailField.disable();
        } else {
          this.businessLineField.enable();
          this.emailField.enable();
        }
        //this.isLoadingBusiness.set(false);
        //this.isRegisteredBusiness.set(response.exists);
        // Se espera un tiempo sino dropdown será undefined..
        setTimeout(() => this.dropdown.focus(), 50);
      },
      error: () => {
        // this.isLoadedBusiness.set(false);
        //this.isRegisteredBusiness.set(false);
        //this.isLoadingBusiness.set(false);
        //this.invoiceAddresses.set([]);
      },
    });
  }*/

  /**
   * Obtener giros comerciales existentes en ecommerce.
   * - Utilizado para crear una empresa sin autocompletado desde el servidor.
   * - Actualmente se utiliza solo en Perú.
   */
  private fetchInternalBusinessLines(): void {
    this.isLoading.set(true);
    this.customerBusinessLineApiService.getBusinessLines().subscribe({
      next: (businessLines) => {
        this.internalBusinessLines.set(businessLines);
        //this.businessLines.set(businessLines);
        this.isLoading.set(false);
        this.businessLineField.enable();
      },
      error: (error) => {
        console.error('Error fetching business lines: ', error);
        this.businessLineField.enable();
        /*this.messageService.add({
          severity: 'error',
          summary: 'Error al obtener giros comerciales',
          detail: 'Ocurrió un error al obtener los giros comerciales.',
        });*/
        this.isLoading.set(false);
      },
    });
  }
}
